import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { SensitiveDataProtection } from '@/components/common/ProtectedComponent';

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    label: string;
  };
  icon: LucideIcon;
  color: 'primary' | 'success' | 'warning' | 'red';
  loading?: boolean;
  trend?: 'up' | 'down' | 'neutral';
  // Permission control
  sensitive?: boolean;
  sensitiveLevel?: 'financial' | 'management' | 'system';
}

const colorClasses = {
  primary: {
    bg: 'bg-primary-50',
    icon: 'text-primary-600',
    trend: 'text-primary-600',
  },
  success: {
    bg: 'bg-success-50',
    icon: 'text-success-600',
    trend: 'text-success-600',
  },
  warning: {
    bg: 'bg-warning-50',
    icon: 'text-warning-600',
    trend: 'text-warning-600',
  },
  red: {
    bg: 'bg-red-50',
    icon: 'text-red-600',
    trend: 'text-red-600',
  },
};

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  color,
  loading = false,
  trend = 'neutral',
  sensitive = false,
  sensitiveLevel = 'financial',
}) => {
  const { t } = useTranslation();
  const colors = colorClasses[color];

  if (loading) {
    return (
      <div className="card card-comfortable">
        <div className="flex items-center justify-between">
          <div className="space-y-3 flex-1">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 rounded animate-pulse w-2/3"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3"></div>
          </div>
          <div className="h-12 w-12 bg-gray-200 rounded-xl animate-pulse"></div>
        </div>
      </div>
    );
  }

  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -2 }}
      className="card card-comfortable hover-lift cursor-pointer group"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
          
          <p className="text-3xl font-bold text-gray-900 mb-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          
          {change && (
            <div className="flex items-center gap-1">
              <span
                className={clsx(
                  "text-sm font-medium",
                  trend === 'up' && colors.trend,
                  trend === 'down' && "text-red-600",
                  trend === 'neutral' && "text-gray-500"
                )}
              >
                {trend === 'up' && '+'}
                {change.value > 0 && trend !== 'down' && '+'}
                {change.value}%
              </span>
              <span className="text-sm text-gray-500">{change.label}</span>
            </div>
          )}
        </div>

        <motion.div
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
          className={clsx(
            "flex items-center justify-center w-12 h-12 rounded-xl transition-colors duration-200",
            colors.bg,
            "group-hover:shadow-medium"
          )}
        >
          <Icon className={clsx("h-6 w-6", colors.icon)} />
        </motion.div>
      </div>
    </motion.div>
  );

  // If sensitive data, use permission protection
  if (sensitive) {
    const sensitiveCardContent = (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="card card-comfortable"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
            <p className="text-3xl font-bold text-gray-400 mb-1">
              ***
            </p>
            <div className="text-xs text-gray-400">
{t('errors.unauthorized')}
            </div>
          </div>

          <div className={clsx(
            "flex items-center justify-center w-12 h-12 rounded-xl",
            "bg-gray-100"
          )}>
            <Icon className="h-6 w-6 text-gray-400" />
          </div>
        </div>
      </motion.div>
    );

    return (
      <SensitiveDataProtection 
        level={sensitiveLevel}
        placeholder={sensitiveCardContent}
      >
        {cardContent}
      </SensitiveDataProtection>
    );
  }

  return cardContent;
};

export default StatsCard;